// =======================================
// modules/reveal/reveal.js
// スクロール表示アニメーション（js-reveal / js-reveal-item）
//
// - .js-reveal  : セクション単位の一括フェードイン
// - .js-reveal-item : アイテム単位（stagger は HTML 側の transition-delay で制御）
// - is-reveal-ready : html に付与（CSS の初期非表示を有効化）
// - is-revealed     : 要素に付与（表示状態）
// =======================================

import { prefersReducedMotion } from '../../core/prefers-reduced-motion.js';

const SELECTOR    = '.js-reveal, .js-reveal-item';
const READY_CLASS = 'is-reveal-ready';
const IN_CLASS    = 'is-revealed';

// 現在のビューポート内（または上方向）にある要素を即時表示
function revealVisible(targets) {
  const vh = window.innerHeight;
  targets.forEach(el => {
    if (!el.classList.contains(IN_CLASS)) {
      if (el.getBoundingClientRect().top < vh) {
        el.classList.add(IN_CLASS);
      }
    }
  });
}

/**
 * スクロール表示アニメーションを起動する。
 * .js-reveal / .js-reveal-item 要素が存在しないページでは即返る。
 *
 * @param {ParentNode} [root=document]
 * @returns {() => void} cleanup
 */
export function initReveal(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return () => {};

  const targets = Array.from(root.querySelectorAll(SELECTOR));
  if (targets.length === 0) return () => {};

  // reduced-motion : アニメーション不要、即表示
  if (prefersReducedMotion()) {
    targets.forEach(el => el.classList.add(IN_CLASS));
    return () => {};
  }

  // IntersectionObserver 非対応 : 即表示
  if (typeof IntersectionObserver === 'undefined') {
    targets.forEach(el => el.classList.add(IN_CLASS));
    return () => {};
  }

  // ① ビューポート内の要素に is-revealed を付与してから is-reveal-ready を付与することで
  //    「一瞬消える」フラッシュを防ぐ（CSS の非表示が適用される前に表示状態を確定する）
  const vh = window.innerHeight;
  const deferred = [];
  targets.forEach(el => {
    if (el.getBoundingClientRect().top < vh) {
      el.classList.add(IN_CLASS);
    } else {
      deferred.push(el);
    }
  });

  // ② CSS の初期非表示を有効化（deferred 要素のみが隠れる）
  document.documentElement.classList.add(READY_CLASS);

  // ③ ビューポート外の要素だけを監視
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add(IN_CLASS);
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0, rootMargin: '0px 0px 0px 0px' }
  );
  deferred.forEach(el => observer.observe(el));

  // ④ bfcache 復元（ブラウザの戻る・進む）時にスクロール位置より上の要素を即時表示
  const onPageShow = (event) => {
    if (event.persisted) {
      revealVisible(targets);
    }
  };
  window.addEventListener('pageshow', onPageShow);

  return () => {
    observer.disconnect();
    window.removeEventListener('pageshow', onPageShow);
  };
}
